import {
  readPropertyMediaGroups,
  readPropertyMediaItems,
  type MediaScope,
  type MediaTarget,
} from '@/api/core/property-media';
import { readMediaEditContext, retireMedia } from '@/api/core/property-media-edit';
import { isApiError } from '@/api/http/errors';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useRef, useState } from 'react';
import type { PropertyRuntime } from '../property-queries';
import type { EditPending } from './media-edit-form';
import {
  canManageMedia,
  useMediaScope,
  useMediaWriteOwner,
  verifyMediaActor,
} from './media-write-form';

export function MediaRetirementForm({
  runtime,
  generation,
  propertyId,
  target,
  collectionId,
  itemId = null,
  isCurrent,
  onSaved,
  retryOnly = false,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  target: MediaTarget;
  collectionId: string;
  itemId?: string | null;
  isCurrent: () => boolean;
  onSaved: () => void | Promise<void>;
  retryOnly?: boolean;
}) {
  const owner = useMediaWriteOwner();
  const { mounted, track } = useMediaScope(owner);
  const [prepared, setPrepared] = useState<{ command: EditPending; epoch: number } | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const pending = owner.editAttempt;
  const mine =
    pending?.retirement &&
    pending.collectionId === collectionId &&
    pending.itemId === (itemId ?? '')
      ? pending
      : null;
  const blocked =
    busy || !!owner.progress || !!owner.attempt || !!owner.createAttempt || (!!pending && !mine);
  const matches = (scope: MediaScope) =>
    scope.propertyId === propertyId &&
    scope.targetKind === target.kind &&
    scope.targetId === target.id &&
    scope.floor === target.floor;
  const run = async (command: EditPending, prepare = false) => {
    if (blocked || !canManageMedia(runtime, generation) || !isCurrent()) return;
    const lease = owner.claim(command);
    if (!lease) return;
    track(lease);
    const current = () =>
      mounted() && isCurrent() && owner.current(lease) && canManageMedia(runtime, generation);
    const retirement = command.retirement;
    if (!retirement) {
      owner.clear(lease);
      return;
    }
    setBusy(true);
    setError(false);
    setMessage(prepare ? '삭제 대상 확인 중…' : '삭제 결과 확인 중…');
    try {
      const actor = await verifyMediaActor(runtime, generation);
      if (!current()) return;
      if (!actor || !owner.bind(lease, actor)) throw new Error('actor');
      if (prepare) {
        if (itemId) {
          const c = await readMediaEditContext(runtime.client, collectionId, itemId);
          if (!current()) return;
          if (
            c.owner.property_id !== propertyId ||
            (target.kind === 'PROPERTY'
              ? c.owner.scope_kind !== 'PROPERTY'
              : target.kind === 'ROOM_TYPE'
                ? c.owner.room_type_id !== target.id
                : c.owner.unit_id !== target.id)
          )
            throw new Error('scope');
          retirement.body.expected_collection_revision = c.expected_collection_revision;
          retirement.body.expected_item_revision = c.expected_item_revision;
        } else {
          const page = await readPropertyMediaItems(
            runtime.client,
            propertyId,
            collectionId,
            { direction: 'asc' },
            undefined,
            true,
          );
          if (!current()) return;
          if (
            !matches(page.scope) ||
            page.group.id !== collectionId ||
            !page.group.collectionRevision
          )
            throw new Error('scope');
          if (page.items.length || page.page.hasMore || page.page.total !== 0) {
            owner.clear(lease);
            setError(true);
            setMessage('모음에 남은 사진을 먼저 삭제해 주세요.');
            return;
          }
          retirement.body.expected_collection_revision = page.group.collectionRevision;
        }
        owner.clear(lease);
        setPrepared({ command, epoch: owner.epoch() });
        setMessage('');
        return;
      }
      if (!retirement.accepted) {
        lease.sent = true;
        await retireMedia(runtime.client, collectionId, itemId, retirement.body);
        if (!current()) return;
        retirement.accepted = true;
      }
      owner.checking(lease);
      if (itemId) {
        try {
          await readMediaEditContext(runtime.client, collectionId, itemId);
          throw new Error('Still present');
        } catch (e) {
          if (!isApiError(e) || e.status !== 404) throw e;
        }
      } else {
        let cursor: string | null = null;
        const seen = new Set<string>();
        do {
          const page = await readPropertyMediaGroups(
            runtime.client,
            propertyId,
            target,
            { direction: 'asc', cursor },
            undefined,
            true,
          );
          if (!current()) return;
          if (!matches(page.scope) || page.groups.some((g) => g.id === collectionId))
            throw new Error('Still present');
          cursor = page.page.nextCursor;
          if (cursor && seen.has(cursor)) throw new Error('cursor');
          if (cursor) seen.add(cursor);
        } while (cursor);
      }
      if (!current()) return;
      owner.clear(lease);
      await onSaved();
      setPrepared(null);
      setMessage(itemId ? '사진 삭제 결과를 확인했습니다.' : '빈 모음 삭제 결과를 확인했습니다.');
      button.current?.focus();
    } catch (e) {
      if (!current()) return;
      setError(true);
      if (isApiError(e) && [403, 404, 409, 422].includes(e.status ?? 0) && !retirement.accepted) {
        owner.clear(lease);
        setPrepared(null);
        if (e.status === 403) runtime.session.reportDomainAccessDenied('property', generation);
        setMessage(
          e.code === 'MEDIA_REFERENCED'
            ? '숨김 설정을 포함해 사용 중인 사진입니다. 사진 편집에서 선택을 먼저 제외하고 저장해 주세요. 작성 중인 편집은 유지됩니다.'
            : e.code === 'MEDIA_COLLECTION_NOT_EMPTY'
              ? '모음에 남은 사진을 먼저 삭제해 주세요.'
              : e.status === 409
                ? '현재 사진 정보가 바뀌었습니다. 삭제 대상을 다시 확인해 주세요.'
                : e.status === 403
                  ? '사진 삭제 권한을 확인해 주세요.'
                  : e.status === 404
                    ? '현재 서버에서 삭제를 지원하지 않거나 대상이 없습니다.'
                    : '삭제 입력 정보를 다시 확인해 주세요.',
        );
      } else {
        setMessage('삭제 결과를 확인하지 못했습니다. 같은 삭제 요청을 다시 시도해 주세요.');
      }
    } finally {
      owner.release(lease);
      if (mounted()) setBusy(false);
    }
  };
  if (!canManageMedia(runtime, generation)) return error ? <p role="alert">{message}</p> : null;
  const confirmation = prepared?.epoch === owner.epoch() ? prepared.command : null;
  return (
    <div className="grid gap-2" aria-busy={busy}>
      {!retryOnly && (
        <Button
          ref={button}
          variant="outline"
          disabled={blocked || !!mine}
          onClick={() =>
            void run(
              {
                edit: true,
                collectionId,
                itemId: itemId ?? '',
                stage: 'read',
                retirement: {
                  target,
                  body: { command_id: crypto.randomUUID(), expected_collection_revision: 1 },
                  accepted: false,
                },
              },
              true,
            )
          }
        >
          {itemId ? '사진 삭제' : '빈 모음 삭제'}
        </Button>
      )}
      {confirmation && !mine && (
        <div className="grid gap-2 rounded-md border p-3">
          <p>
            {itemId
              ? '등록된 사진을 삭제할까요? 관리 목록에서 제외되며 파일과 삭제 이력은 보관됩니다. 사용 중인 사진은 먼저 선택에서 제외해야 합니다.'
              : '빈 사진 모음을 삭제할까요? 관리 목록에서 제외되며 삭제 이력은 보관됩니다.'}
          </p>
          <Button disabled={blocked} onClick={() => void run(confirmation)}>
            삭제 확인
          </Button>
          <Button
            variant="outline"
            disabled={blocked}
            onClick={() => {
              setPrepared(null);
              button.current?.focus();
            }}
          >
            취소
          </Button>
        </div>
      )}
      {message && <p role={error ? 'alert' : 'status'}>{message}</p>}
      {mine && !busy && !owner.progress && (
        <Button variant="outline" disabled={blocked} onClick={() => void run(mine)}>
          같은 삭제 요청 재시도
        </Button>
      )}
    </div>
  );
}

// Retained commands outlive their list rows; omission cannot settle an uncertain POST.
export function MediaRetirementRecovery({
  visibleCollectionId,
  visibleItems,
  onSaved,
  ...props
}: Omit<
  Parameters<typeof MediaRetirementForm>[0],
  'collectionId' | 'itemId' | 'retryOnly' | 'onSaved'
> & {
  visibleCollectionId?: string | null;
  visibleItems?: { collectionId: string; items: readonly { id: string }[] };
  onSaved: (command: EditPending) => void | Promise<void>;
}) {
  const pending = useMediaWriteOwner().editAttempt;
  const target = pending?.retirement?.target;
  if (
    !pending ||
    !target ||
    pending.collectionId === visibleCollectionId ||
    (visibleItems &&
      (pending.collectionId !== visibleItems.collectionId ||
        !pending.itemId ||
        visibleItems.items.some((item) => item.id === pending.itemId))) ||
    target.kind !== props.target.kind ||
    target.id !== props.target.id ||
    target.floor !== props.target.floor
  )
    return null;
  return (
    <MediaRetirementForm
      {...props}
      collectionId={pending.collectionId}
      itemId={pending.itemId || null}
      retryOnly
      onSaved={() => onSaved(pending)}
    />
  );
}
